import { Gauge, Cloud, Boxes, Code2, FileCode2, ArrowUpRight, Server, Bot, RefreshCcw } from "lucide-react";
import { SectionHead, Stagger, StaggerItem } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const SERVICES = [
  {
    icon: Gauge,
    title: "Cloud Management & Optimization",
    desc: "Multi-cloud orchestration, cost optimization, and governance across Azure, AWS & Google Cloud.",
    features: ["Cloud cost analysis and optimization", "Resource governance and compliance", "Multi-cloud visibility and management"],
    cta: "Learn More",
  },
  {
    icon: Cloud,
    title: "Azure Solutions",
    desc: "Enterprise-grade Azure infrastructure for startups and enterprises.",
    features: ["App Service & Kubernetes (AKS)", "Azure SQL Database & CosmosDB", "Azure DevOps and CI/CD pipelines", "Managed identity and security"],
    cta: "Explore Azure Services",
  },
  {
    icon: Server,
    title: "AWS Solutions",
    desc: "Scalable AWS infrastructure with best-practice implementations.",
    features: ["EC2, RDS, Lambda functions", "S3 storage and CloudFront CDN", "ECS & EKS container orchestration", "Security and compliance management"],
    cta: "Explore AWS Services",
  },
  {
    icon: Boxes,
    title: "Microservices Architecture",
    desc: "Ship features faster with containerized services that scale independently.",
    features: ["Docker containerization", "Kubernetes orchestration (AKS, EKS)", "Service mesh (Istio, Linkerd)", "API gateway and service communication"],
    cta: "Learn More",
  },
  {
    icon: Code2,
    title: "Web Development & API Services",
    desc: "Web applications your customers rely on, engineered for scale from day one.",
    features: ["React, Vue, Angular frontends", "Node.js, .NET, Python backends", "GraphQL and RESTful APIs", "Serverless and containerized deployments"],
    cta: "Discuss a Project",
  },
  {
    icon: FileCode2,
    title: "Infrastructure as Code",
    desc: "Environments that provision in minutes — identical, repeatable, every time.",
    features: ["Infrastructure automation", "Configuration management", "Environment parity and GitOps", "Disaster recovery and backup strategies"],
    cta: "Learn More",
  },
  {
    icon: Bot,
    title: "AI & Business Automation",
    desc: "Practical AI and automation that removes busywork and speeds up decisions.",
    features: ["AI assistants and copilots", "Workflow and approval automation", "Document and data processing", "Chatbots and system integrations"],
    cta: "Learn More",
  },
  {
    icon: RefreshCcw,
    title: "Application & Technology Modernization",
    desc: "Modernize legacy applications and engineer custom software products.",
    features: ["Legacy application modernization", "Replatforming to cloud-native", "Custom product engineering", "API-first integration"],
    cta: "Learn More",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32 bg-nexblue overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="orbit-ring w-[720px] h-[720px] -left-72 top-16" style={{ borderColor: "rgba(0,168,232,0.10)" }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(0,168,232,0.10),transparent_55%)]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHead
          dark
          chapter="02"
          overline="What We Do"
          title="Our Cloud Services"
          sub="Eight practice areas covering everything from first architecture to day-two operations."
          testId="services-heading"
        />
        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map(({ icon: Icon, title, desc, features, cta }, i) => (
            <StaggerItem key={title}>
              <div
                data-testid={`service-card-${i + 1}`}
                className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1.5 hover:border-nexcyan/50 hover:bg-white/[0.07]"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-nexcyan/15 text-nexcyan transition-colors duration-300 group-hover:bg-nexorange group-hover:text-nexblue">
                  <Icon size={22} />
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-white">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{desc}</p>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/70">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-nexorange" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => scrollToId("#contact")}
                  data-testid={`service-cta-${i + 1}`}
                  className="mt-7 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-nexcyan transition-colors duration-300 hover:text-nexorange"
                >
                  {cta}
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
